// Quota enforcement — when a quota's conditionGroup matches the user's
// current responses AND its currentCount has reached the limit, the survey
// takes the configured action (close or jump to thank-you).
//
// `currentCount` is populated server-side at fetch time. Until the backend
// wires the aggregation it'll be 0 and no quota fires, which is the safe
// default (over-collect rather than under-collect).

import type { LogicConditionGroup, SurveyQuota } from '../types.js';
import { evaluateConditionGroup } from './logic.js';

export type QuotaHit =
  | { hit: true; action: 'closeSurvey' | 'skipToThankYou'; quota: SurveyQuota }
  | { hit: false };

interface CheckQuotasParams {
  quotas: SurveyQuota[] | undefined;
  responses: Record<string, unknown>;
  variables: Record<string, unknown>;
}

// Returns the first quota that matches AND is at-or-over its limit. The
// caller fires the action — typically right after an `answered` event so
// the user is routed off the survey before they keep answering.
export function checkQuotas({ quotas, responses, variables }: CheckQuotasParams): QuotaHit {
  if (!quotas || quotas.length === 0) return { hit: false };
  for (const quota of quotas) {
    if (!quota || typeof quota.limit !== 'number') continue;
    const currentCount = quota.currentCount ?? 0;
    if (currentCount < quota.limit) continue;
    if (!conditionMatches(quota.conditionGroup, responses, variables)) continue;
    return { hit: true, action: quota.action, quota };
  }
  return { hit: false };
}

function conditionMatches(
  group: LogicConditionGroup | undefined,
  responses: Record<string, unknown>,
  variables: Record<string, unknown>,
): boolean {
  if (!group) return true; // a quota without conditions matches every response
  return evaluateConditionGroup(group, { responses, variables });
}
